import jsPDF from 'jspdf';
import { PdfConfig, PdfHeaderData } from '@/types/pdf';
import { renderPdfHeader } from '../components/pdfHeader';
import { renderPdfFooter } from '../components/pdfFooter';

export class BasePdfTemplate {
  protected doc: jsPDF;
  protected config: PdfConfig;
  protected margins = {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10
  };
  
  protected pageHeight: number;
  protected pageWidth: number;
  protected currentY: number;

  constructor(config: Partial<PdfConfig> = {}) {
    this.config = {
      format: config.format || 'a4',
      orientation: config.orientation || 'portrait',
      unit: 'mm',
      compress: true,
      ...config
    };

    this.doc = new jsPDF(this.config);
    
    // Dimensões da página
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.currentY = this.margins.top;
  }

  /**
   * Renderiza cabeçalho padrão
   */
  protected renderHeader(headerData: PdfHeaderData): void {
    this.currentY = renderPdfHeader(this.doc, headerData, this.margins);
  }

  /**
   * Renderiza rodapé padrão
   */
  protected renderFooter(pageNumber: number, totalPages: number, geradoPor?: string): void {
    renderPdfFooter(this.doc, pageNumber, totalPages, this.margins, geradoPor);
  }

  /**
   * Verifica se precisa adicionar nova página
   */
  protected checkPageBreak(requiredSpace: number): void {
    const availableSpace = this.pageHeight - this.currentY - this.margins.bottom;
    
    if (availableSpace < requiredSpace) {
      this.doc.addPage();
      this.currentY = this.margins.top;
    }
  }

  /**
   * Adiciona texto com quebra automática
   */
  protected addText(
    text: string, 
    x: number, 
    fontSize: number = 10,
    style: 'normal' | 'bold' = 'normal',
    maxWidthOverride?: number
  ): void {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', style);
    
    const maxWidth = maxWidthOverride || (this.pageWidth - x - this.margins.right);
    const lines = this.doc.splitTextToSize(text || '', maxWidth);
    
    lines.forEach((line: string) => {
      this.checkPageBreak(7);
      this.doc.text(line, x, this.currentY);
      this.currentY += 7;
    });
  }

  /**
   * Adiciona linha horizontal
   */
  protected addLine(y?: number): void {
    const lineY = y || this.currentY;
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(
      this.margins.left,
      lineY,
      this.pageWidth - this.margins.right,
      lineY
    );
  }

  /**
   * Retorna o PDF como blob
   */
  public getBlob(): Blob {
    return this.doc.output('blob');
  }

  /**
   * Retorna tamanho do PDF em KB
   */
  public getSizeInKB(): number {
    const blob = this.getBlob();
    return Math.round(blob.size / 1024);
  }

  /**
   * Baixa o PDF
   */
  public download(filename: string): void {
    this.doc.save(filename);
  }
}
